import React, {useState} from 'react';
import {
    GridItem,
    TextStyled,
    ButtonStyled,
    RadioSetStyled,
} from '@nike/nike-design-system-components';
import {LevelSelectionProps} from '../../types/LevelSelectionProps';
import CustomRadioStyled from './CustomRadioStyled';
import Divider from '../Divider/Divider';
import ButtonContainer from '../ButtonContainer/ButtonContainer';
import StyledModal from '../ModalStyled/StyledModal';
import Actions from '../ModalStyled/Actions';
import {ContentModalLevel3, ContentModalSuccess} from '../../content/modals';
import Spinner from '../Spinner/Spinner';
import type {BatchReceiverRequest, BatchReceiverResponse} from '../../api/batchReceiver';
import {batchReceiver} from "../../api/batchReceiver";
import ContentModalError from "../../content/modals/ContentModalError";
import { getErrorMessage, getErroneousIdsOrEmails } from './LevelSelection.util';

const LevelSelection: React.FC<LevelSelectionProps> = ({
        handleValidation,
        onError,
        onSuccess, 
        textData
    }) => {
    const [selectedValue, setSelectedValue] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [modalContent, setModalContent] = useState<{
        title: string;
        content: JSX.Element | null;
        slots?: { FooterSlot?: JSX.Element };
        icon?: () => JSX.Element;
    }>({
        title: '',
        content: null
    });

    const [isSubmissionProcess, setIsSubmissionProcess] = useState(false);
    const [isWarningAlert, setIsWarningAlert] = useState(false);
    const isEmpty = textData.length === 0;


    const displaySuccess = () => {
        setIsSubmissionProcess(false);
        setIsWarningAlert(false);
        setModalContent({
            title: 'Request Submitted',
            content: <ContentModalSuccess/>,
        });
        onSuccess();
    };


    const displayError = ( result: BatchReceiverResponse ) => {
        let errorMsg = getErrorMessage(result);
        let erroneousIdsOrEmails = getErroneousIdsOrEmails(result);

        setModalContent({
        title: `Error occurred with submission: ${result?.errors?.join(', ')}`,
        content:
        <>
            <TextStyled display="block" bold={true} >{errorMsg + " Click to copy:"}</TextStyled>
            <button
                style={{
                    color: '#1151FF',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    background: 'none',
                    border: 'none',
                    padding: 10,
                    font: 'inherit'
                }}
                onMouseOver={(e) => e.currentTarget.style.textDecoration = 'underline'}
                onMouseOut={(e) => e.currentTarget.style.textDecoration = 'none'}
                onClick={() => navigator.clipboard.writeText(erroneousIdsOrEmails as string)}
            >
                {erroneousIdsOrEmails}
            </button>
        </>,
        slots: {
            FooterSlot: (
                <Actions
                    closeModal={closeModal}
                    handleActiveClick={closeModal}
                />
            ),
        }})
        setIsSubmissionProcess(false);
        setIsWarningAlert(true);
        setIsModalOpen(true);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedValue(event.target.value);
        if (event.target.value === 'ACCESS') {
            setModalContent({
                title: 'Are you sure you want to proceed?',
                content: <ContentModalLevel3/>,
                slots: {
                    FooterSlot: (
                        <Actions
                            closeModal={closeModal}
                            handleActiveClick={() => handleActiveClick(event.target.value)}
                        />
                    ),
                },
            });
            setIsSubmissionProcess(false);
            setIsWarningAlert(true);
            setIsModalOpen(true);
        }
    };

    const handleActiveClick = async (lockoutLevel: string | null) => {
        //Make the submit button inactive
        setIsSubmissionProcess(true);
        //Validation before sending the request
        const batchRequest: BatchReceiverRequest | null = handleValidation(lockoutLevel);
        if (!batchRequest || (batchRequest && batchRequest.consumerIds.length <= 0)) {
            setIsSubmissionProcess(false);
            setIsModalOpen(false); //Close level 3 warning modal if it's open
            setIsWarningAlert(false); //Disable level 3 warning modal if it was open
            setSelectedValue(null);
            return;
        }

        //Activate modal and start sending the request
        setModalContent({
            title: 'UPMID(s) Submission Processing',
            content: null,
            icon: () => <Spinner size="lg" className="loading-animation"/>,
        });
        setIsWarningAlert(false);
        setIsModalOpen(true);

        try {
            const result = await batchReceiver(batchRequest);

            //Handle response
            if (result?.errors && result.errors.length === 0) {
                displaySuccess();
            } else {
                displayError(result);
                onError(`Error occurred with submission: ${result?.errors?.join(', ')}`);
            }
            setSelectedValue(null);
        } catch(error){
            setIsSubmissionProcess(false);
            setIsWarningAlert(false);
            setModalContent({
                title: 'Request Failed',
                content: <ContentModalError/>,
            });
        }
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedValue(null);
    };

    const modalClasses = `${isSubmissionProcess ? 'submission-process' : ''} ${
        isWarningAlert ? 'warning-alert' : ''
    }`;

    return (
        <>
            <Divider/>
            <GridItem xs={{
                colStart: 1,
                colEnd: 6
            }} justifySelf="start">
                <TextStyled display="block" bold={true} >
                    Select Lockout Level
                </TextStyled>
                <TextStyled 
                >
                    Manage UPMID(s) and/or Email(s) by selecting the desired Lockout Level
                </TextStyled>
            </GridItem>
            <GridItem xs={6}>
                <RadioSetStyled legend="" name="name" onChange={handleChange} selectedValue={selectedValue || ''}>
                    <CustomRadioStyled
                        id="id1"
                        label="Level 1"
                        afterLabel="Deletes a user active sessions and invalidates all refresh tokens"
                        value="SESSION"
                    />
                    <CustomRadioStyled
                        id="id2"
                        label="Level 2"
                        afterLabel="Forces the user to reset their password as well as everything in Level 1"
                        value="PASSWORD"
                    />
                    <CustomRadioStyled
                        id="id3"
                        label="Level 3"
                        afterLabel="Permanently locks the user out of their account (sets denyLogin flag in Identity) as well as everything in Level 1"
                        value="ACCESS"
                    />
                </RadioSetStyled>
            </GridItem>

            <Divider/>

            <ButtonContainer xs={12} justifySelf="end">
                <ButtonStyled
                    appearance="primary"
                    size="medium"
                    disabled={!selectedValue || isSubmissionProcess || isEmpty}
                    onClick={() => handleActiveClick(selectedValue)}
                >
                    Process
                </ButtonStyled>
            </ButtonContainer>

            <StyledModal
                appearance="baseMedium"
                title={modalContent.title}
                isOpen={isModalOpen}
                closeModal={closeModal}
                slots={modalContent.slots}
                Icon={modalContent.icon}
                classes={{
                    main: modalClasses,
                }}
                onResize={() => {}}
                onResizeCapture={() => {}}
            >
                {modalContent.content}
            </StyledModal>
            <div id="modal-root"/>
        </>
    );
};

export default LevelSelection;
